import React, { useEffect, useState } from "react";
import { api } from "../lib/api";

function Cell({ label, value, mono = false }) {
  return (
    <div>
      <p className="eyebrow">{label}</p>
      <p className={`text-[var(--ink-heading)] mt-1.5 ${mono ? "font-mono-ui text-xs" : "text-base"}`}>
        {String(value)}
      </p>
    </div>
  );
}

export default function Diagnostics() {
  const [diag, setDiag] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.katlasDiagnostics().then(setDiag).catch((e) => setErr(e.message));
  }, []);

  return (
    <main className="max-w-4xl mx-auto px-6 lg:px-10 py-16" data-testid="diagnostics-page">
      <header className="mb-10">
        <p className="eyebrow">Operational</p>
        <h1 className="font-display text-3xl md:text-4xl text-[var(--ink-heading)] mt-3 leading-tight">
          KATLAS diagnostics
        </h1>
        <p className="text-[var(--ink-secondary)] mt-3 text-sm max-w-2xl leading-relaxed">
          Redacted view. Bearer token values, Authorization headers, signed
          envelopes and raw evidence are never displayed.
        </p>
      </header>

      {err && <p className="text-red-700" data-testid="diagnostics-error">{err}</p>}
      {!diag && !err && <p className="text-[var(--ink-muted)]" data-testid="diagnostics-loading">Checking node…</p>}

      {diag && (
        <section className="border border-slate-200 bg-white p-7 space-y-7" data-testid="diagnostics-content">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Cell label="KATLAS mode" value={diag.katlas_mode} mono />
            <Cell label="Node URL present" value={String(diag.node_url_present)} mono />
            <Cell label="Bearer token present" value={String(diag.katlas_bearer_token_present)} mono />
            <Cell label="Token source used" value={diag.token_source_used} mono />
            <Cell label="Allow simulation fallback" value={String(diag.allow_simulation_fallback)} mono />
            <Cell label="Timeout (s)" value={String(diag.timeout_seconds)} mono />
            {diag.status && <Cell label="Status" value={diag.status} mono />}
            {diag.label && <Cell label="Label" value={diag.label} />}
          </div>

          <div className="border-t border-slate-200 pt-6">
            <p className="eyebrow mb-3">Runtime visibility · matching env keys</p>
            <table className="w-full text-sm" data-testid="env-key-table">
              <thead>
                <tr className="text-left text-[var(--ink-muted)]">
                  <th className="py-2 pr-4 font-mono-ui text-[10.5px] uppercase tracking-[0.14em]">Key</th>
                  <th className="py-2 pr-4 font-mono-ui text-[10.5px] uppercase tracking-[0.14em]">Present</th>
                  <th className="py-2 pr-4 font-mono-ui text-[10.5px] uppercase tracking-[0.14em]">Value length</th>
                  <th className="py-2 pr-4 font-mono-ui text-[10.5px] uppercase tracking-[0.14em]">Non empty</th>
                </tr>
              </thead>
              <tbody>
                {(diag.runtime_visibility?.matching_env_keys || []).map((k) => (
                  <tr key={k.name} className="border-t border-slate-100" data-testid={`env-row-${k.name}`}>
                    <td className="py-2 pr-4 font-mono-ui text-xs">{k.name}</td>
                    <td className="py-2 pr-4 font-mono-ui text-xs">{String(k.present)}</td>
                    <td className="py-2 pr-4 font-mono-ui text-xs">{k.value_length}</td>
                    <td className="py-2 pr-4 font-mono-ui text-xs">{String(k.non_empty)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-[var(--ink-muted)] mt-4">
              Stage 1 is correct when KATLAS_BEARER_TOKEN is present with
              value_length: 0 and non_empty: false. Stage 2 activation is
              performed via secure runtime injection by the production
              operator.
            </p>
          </div>
        </section>
      )}
    </main>
  );
}
